// Pure helpers for the Google Workspace UI. No imports so they run under `deno test`.

/** Error from the google-workspace edge function; `code` is e.g. needs_reauth | missing_send_scope | sync_in_progress. */
export class GoogleWorkspaceError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'GoogleWorkspaceError';
    this.code = code;
    this.status = status;
  }
}

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Builds an error from a parsed JSON error body (`{error, code?}`); anything else becomes a generic message. */
export function errorFromBody(body: unknown, status?: number): GoogleWorkspaceError {
  const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; code?: unknown };
  const message = typeof b.error === 'string' && b.error.length > 0 ? b.error : GENERIC_ERROR;
  return new GoogleWorkspaceError(message, typeof b.code === 'string' ? b.code : undefined, status);
}

/** Only ever navigate the browser to Google's consent screen. */
export function isGoogleAuthUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && u.hostname === 'accounts.google.com';
  } catch {
    return false;
  }
}

const BROAD = new Set(['INBOX', 'SENT', 'IMPORTANT', 'STARRED', 'UNREAD', 'DRAFT', 'CHAT']);

/** System labels that would sync a lot of unrelated mail; the UI warns before allowing them. */
export function isBroadSystemLabel(l: { id: string; type: string }): boolean {
  return l.type === 'system' && (BROAD.has(l.id) || l.id.startsWith('CATEGORY_'));
}

export const isGoogleEmail = (source: string | null | undefined): boolean => source === 'google_gmail';


/** True for meetings_sync rows synced from Google (google_meet / google_calendar). */
export function isGoogleMeetingSource(source: string | null | undefined): boolean {
  return typeof source === 'string' && source.startsWith('google_');
}

/** Friendly label for a meetings_sync.source value. */
export function meetingSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case 'google_meet': return 'Google Meet';
    case 'google_calendar': return 'Google Calendar';
    case 'local_recorder': return 'Recorded';
    case 'microsoft_teams':
    case 'teams':
    default: return 'Teams';
  }
}

export type TranscriptState = 'ready' | 'none' | 'pending' | null;

/**
 * Transcript state shown for Google meetings (null for other sources).
 * 'pending' only once the meeting has ended; a future/ongoing meeting shows nothing.
 */
export function transcriptState(
  source: string | null | undefined,
  recordingStatus: string | null | undefined,
  endTime: string | null | undefined,
  now: Date = new Date(),
): TranscriptState {
  if (!isGoogleMeetingSource(source)) return null;
  if (recordingStatus === 'transcribed') return 'ready';
  if (recordingStatus === 'no_transcript') return 'none';
  const end = endTime ? new Date(endTime).getTime() : NaN;
  return Number.isFinite(end) && end <= now.getTime() ? 'pending' : null;
}
